import * as Yup from 'yup';
import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { Link as RouterLink } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import closeFill from '@iconify/icons-eva/close-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
// material
import {
  Link,
  Stack,
  Alert,
  Checkbox,
  TextField,
  IconButton,
  InputAdornment,
  FormControlLabel
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// routes
import { PATH_AUTH } from '../../../routes/paths';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
//
import { MIconButton } from '../../@material-extend';

// ----------------------------------------------------------------------
type InitialValues = {
  email: string;
  password: string;
  remember: boolean;
  afterSubmit?: string;
};

type ChangePasswordInitialValues = {
  email: string;
  password: string;
  newPassword: string;
  confirmNewPassword: string;
  afterSubmit?: string;
};

export interface LoginFormProps {
  userEmail: undefined | string;
}

export default function LoginForm(props: LoginFormProps) {
  const { login, chagePassword } = useAuth();
  const isMountedRef = useIsMountedRef();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [showPassword, setShowPassword] = useState(false);
  const [chagePasswordNeeded, setChangePasswordNeeded] = useState(false);

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .email('El campo email debe contener una dirección de correo electrónico correcta.')
      .required('El email es obligatorio'),
    password: Yup.string().required('La contraseña es obligatoria.')
  });
  const ChagePasswordShchema = Yup.object().shape({
    email: Yup.string().email('Email must be a valid email address').required('Email is required'),
    password: Yup.string().required('Password is required'),
    newPassword: Yup.string().required('New password is required'),
    confirmNewPassword: Yup.string()
      .required('Confirm new password is required')
      .oneOf([Yup.ref('newPassword'), null], "Passwords don't match")
  });
  const formik = useFormik<InitialValues>({
    initialValues: {
      email: props.userEmail ? props.userEmail : '',
      password: '',
      remember: true
    },
    enableReinitialize: true,
    validationSchema: LoginSchema,
    onSubmit: async (values, { setErrors, setSubmitting, resetForm }) => {
      try {
        await login(values.email, values.password);
        enqueueSnackbar('Login success', {
          variant: 'success',
          action: (key) => (
            <MIconButton size="small" onClick={() => closeSnackbar(key)}>
              <Icon icon={closeFill} />
            </MIconButton>
          )
        });
        if (isMountedRef.current) {
          setSubmitting(false);
        }
      } catch (error) {
        resetForm();
        if (isMountedRef.current) {
          setSubmitting(false);
          if (error.message === 'newPasswordRequired') {
            setChangePasswordNeeded(true);
          } else {
            setErrors({ afterSubmit: error.message });
          }
        }
      }
    }
  });

  const ChangePasswordFormik = useFormik<ChangePasswordInitialValues>({
    initialValues: {
      email: '',
      password: '',
      newPassword: '',
      confirmNewPassword: ''
    },
    validationSchema: ChagePasswordShchema,
    onSubmit: async (values, { setErrors, setSubmitting, resetForm }) => {
      try {
        await chagePassword(values.email, values.password, values.newPassword);
        enqueueSnackbar('Login success', {
          variant: 'success',
          action: (key) => (
            <MIconButton size="small" onClick={() => closeSnackbar(key)}>
              <Icon icon={closeFill} />
            </MIconButton>
          )
        });
        if (isMountedRef.current) {
          setSubmitting(false);
        }
      } catch (error) {
        resetForm();
        if (isMountedRef.current) {
          setSubmitting(false);
          if (error.message === 'newPasswordRequired') {
            setChangePasswordNeeded(true);
          } else {
            setErrors({ afterSubmit: error.message });
          }
        }
      }
    }
  });

  const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps } = formik;

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  if (chagePasswordNeeded) {
    return (
      <FormikProvider value={ChangePasswordFormik}>
        <Form autoComplete="off" noValidate onSubmit={ChangePasswordFormik.handleSubmit}>
          <Stack spacing={3}>
            {ChangePasswordFormik.errors.afterSubmit && (
              <Alert severity="error">{ChangePasswordFormik.errors.afterSubmit}</Alert>
            )}

            <TextField
              fullWidth
              autoComplete="username"
              type="email"
              label="Email address"
              {...ChangePasswordFormik.getFieldProps('email')}
              error={Boolean(
                ChangePasswordFormik.touched.email && ChangePasswordFormik.errors.email
              )}
              helperText={ChangePasswordFormik.touched.email && ChangePasswordFormik.errors.email}
            />

            <TextField
              fullWidth
              autoComplete="current-password"
              type={showPassword ? 'text' : 'password'}
              label="Contraseña"
              {...ChangePasswordFormik.getFieldProps('password')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleShowPassword} edge="end">
                      <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              error={Boolean(
                ChangePasswordFormik.touched.password && ChangePasswordFormik.errors.password
              )}
              helperText={
                ChangePasswordFormik.touched.password && ChangePasswordFormik.errors.password
              }
            />

            <TextField
              fullWidth
              autoComplete="new-password"
              type={showPassword ? 'text' : 'password'}
              label="New Password"
              {...ChangePasswordFormik.getFieldProps('newPassword')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleShowPassword} edge="end">
                      <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              error={Boolean(
                ChangePasswordFormik.touched.newPassword && ChangePasswordFormik.errors.newPassword
              )}
              helperText={
                ChangePasswordFormik.touched.newPassword && ChangePasswordFormik.errors.newPassword
              }
            />

            <TextField
              fullWidth
              autoComplete="confirm-new-password"
              type={showPassword ? 'text' : 'password'}
              label="Confirm new password"
              {...ChangePasswordFormik.getFieldProps('confirmNewPassword')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleShowPassword} edge="end">
                      <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              error={Boolean(
                ChangePasswordFormik.touched.confirmNewPassword &&
                  ChangePasswordFormik.errors.confirmNewPassword
              )}
              helperText={
                ChangePasswordFormik.touched.confirmNewPassword &&
                ChangePasswordFormik.errors.confirmNewPassword
              }
            />
          </Stack>

          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={ChangePasswordFormik.isSubmitting}
            sx={{ my: 2 }}
          >
            Login
          </LoadingButton>
        </Form>
      </FormikProvider>
    );
  }

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {errors.afterSubmit && <Alert severity="error">{errors.afterSubmit}</Alert>}

          <TextField
            fullWidth
            autoComplete="username"
            type="email"
            label="Dirección de correo electrónico"
            {...getFieldProps('email')}
            error={Boolean(touched.email && errors.email)}
            helperText={touched.email && errors.email}
          />

          <TextField
            fullWidth
            autoComplete="current-password"
            type={showPassword ? 'text' : 'password'}
            label="Contraseña"
            {...getFieldProps('password')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleShowPassword} edge="end">
                    <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                  </IconButton>
                </InputAdornment>
              )
            }}
            error={Boolean(touched.password && errors.password)}
            helperText={touched.password && errors.password}
          />
        </Stack>

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }}>
          <FormControlLabel
            control={<Checkbox {...getFieldProps('remember')} checked={values.remember} />}
            label="Recordame"
          />

          <Link component={RouterLink} variant="subtitle2" to={PATH_AUTH.resetPassword}>
            ¿No recuerdas tu contraseña?
          </Link>
        </Stack>

        <LoadingButton
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitting}
        >
          Login
        </LoadingButton>
      </Form>
    </FormikProvider>
  );
}
